/** @jsxImportSource @emotion/react */
import {
  EuiAccordion,
  EuiButton,
  EuiButtonEmpty,
  EuiCheckableCard,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from "@inscopix/ideas-eui";
import { useState } from "react";
import { css } from "@emotion/react";
import { ModalLinkDataTable } from "./ModalLinkDataTable";
import { ModalJoinTable } from "./ModalJoinTable";

const options = [
  {
    id: "link",
    label: "Link Table",
    description: (
      <div>
        <p>
          Add columns from another data table by manually linking rows in this
          table with rows in the other table. This allows you to relate or
          associate data tables through selecting the shared data.
        </p>
        <EuiSpacer size="s" />
        <EuiAccordion
          id={"example-use-case-link-1"}
          buttonContent="Example use case"
        >
          <EuiPanel color="subdued">
            <EuiText size="s" color="subdued">
              <ul>
                <li>
                  You have a ‘Recordings’ Table (where each row contains data
                  from a single miniscope recording session) and a ‘Subjects’
                  Table (where each row contains the information of a mouse used
                  in the study, including their date of birth, sex, genotype).
                </li>
                <li>
                  You want to associate a particular mouse (subject) with each
                  row in your Recordings Table. You can accomplish that here by
                  Linking the tables, which will create a blank column for
                  Subject ID.
                </li>
                <li>
                  You can then select the appropriate Subject ID for each
                  Recording Row.
                </li>
              </ul>
            </EuiText>
          </EuiPanel>
        </EuiAccordion>
      </div>
    ),
  },
  {
    id: "join",
    label: "Join Table",
    description: (
      <div>
        <p>
          Add data from another table via a commonly shared or linked column.
          This ‘join&apos; operation can either be used to 1{`)`} view simple
          relationships between tables as well as to 2{`)`} flexibly group data
          in ways that make it easier to set up analysis tables with
          appropriately mapped input data.
        </p>
        <EuiSpacer size="s" />
        <EuiAccordion
          id={"example-use-case-join-1"}
          buttonContent="Example use case (1)"
        >
          <EuiPanel color="subdued">
            <EuiText size="s" color="subdued">
              <ul>
                <li>
                  You have a ‘Recordings’ Table (where each row contains data
                  from a single miniscope recording session). The Recordings
                  Table includes a column called ‘Subject Name’, which contains
                  the Mouse ID for reach row.
                </li>
                <li>
                  You have also built a ‘Subjects’ Table (where each row
                  contains the information of a mouse used in the study,
                  including their ID, date of birth, sex, genotype).
                </li>
                <li>
                  The ‘ID’ column in this table corresponds to the ‘Mouse ID’
                  column in the Recordings Table.
                </li>
                <li>
                  This ‘join’ operation allows you to add columns like ‘Date of
                  Birth, Sex, and Genotype’ to the Recordings column via the
                  linked ‘ID’ and ‘Mouse ID’ columns.
                </li>
              </ul>
            </EuiText>
          </EuiPanel>
        </EuiAccordion>
        <EuiAccordion
          id={"example-use-case-join-2"}
          buttonContent="Example use case (2)"
        >
          <EuiPanel color="subdued">
            <EuiText size="s" color="subdued">
              <ul>
                <li>
                  You have a Subjects Table where each row contains the
                  information of a mouse used in the study, including their ID,
                  date of birth, sex, genotype.
                </li>
                <li>
                  You also have a Recordings Table, where each Recording table
                  row has been associated with a row in the Subjects table (see
                  Use Case 1 or the Linking Tables use case for examples of how
                  to do that). .
                </li>
                <li>
                  Now you would like to add column in your Subjects Table that
                  shows all the miniscope recordings acquired from each mouse in
                  your study.
                </li>
                <li>
                  You can accomplish that by performing this ‘join’ operation on
                  the Subject ID column and the Recordings&rarr;Subject ID
                  column, and selecting the Recordings&rarr;Miniscope Movie
                  column to add
                </li>
              </ul>
            </EuiText>
          </EuiPanel>
        </EuiAccordion>
      </div>
    ),
  },
];

interface ModalCreateColumnFromDataTableProps {
  onBack?: () => void;
  onClose: () => void;
  tableId: string;
}

/** Component that renders a modal for inserting columns from existing tables */
export const ModalCreateColumnFromExistingTable = ({
  onBack,
  onClose,
  tableId,
}: ModalCreateColumnFromDataTableProps) => {
  const [selectedOption, setSelectedOption] = useState(options[0].id);
  const [checked, setChecked] = useState(false);

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        width: 600px;
      `}
    >
      {checked && selectedOption === "link" && (
        <ModalLinkDataTable
          tableId={tableId}
          onBack={() => setChecked(false)}
          onClose={onClose}
        />
      )}

      {checked && selectedOption === "join" && (
        <ModalJoinTable
          tableId={tableId}
          onBack={() => setChecked(false)}
          onClose={onClose}
        />
      )}

      {!checked && (
        <>
          <EuiModalHeader>
            <EuiModalHeaderTitle component="h3">
              Insert Column(s) from Table
            </EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            {options.map((option, idx, arr) => {
              const isLastOption = idx === arr.length - 1;
              return (
                <>
                  <EuiCheckableCard
                    id={option.id}
                    label={<b>{option.label}</b>}
                    name={option.id}
                    value="radio1"
                    checked={selectedOption === option.id}
                    onChange={() => setSelectedOption(option.id)}
                  >
                    <p>{option.description}</p>
                  </EuiCheckableCard>
                  {!isLastOption && <EuiSpacer size="m" />}
                </>
              );
            })}
          </EuiModalBody>

          <EuiModalFooter>
            {onBack && (
              <EuiButtonEmpty iconType="arrowLeft" onClick={onBack}>
                Back
              </EuiButtonEmpty>
            )}
            <EuiButton type="submit" fill onClick={() => setChecked(true)}>
              Next
            </EuiButton>
          </EuiModalFooter>
        </>
      )}
    </EuiModal>
  );
};
